import { useState, useEffect, useCallback } from 'react';
import { Node, useNodesState, useEdgesState } from 'reactflow';
import { FlowDSL } from '@holloway/dsl';
import { convertDSLToReactFlowNodes } from './utils';
import { detachReferencesTo } from '@/features/editor/utils/detachReferencesTo';
import { stableHash } from '@/features/editor/utils/stableHash';

/**
 * Split a React Flow node-change array into the pieces onNodesChangeWrapped
 * needs.
 *
 * Returns:
 *   `removedIds`      — ids of non-Start nodes being removed.  These are
 *                       passed to detachReferencesTo so that stale branch
 *                       references are cleared at the same time the node is
 *                       removed from state.
 *   `filteredChanges` — the change list with ALL remove entries stripped out.
 *
 * Why strip ALL removes (not just the Start node's)?
 *   Non-Start removals are already applied via
 *   `setNodes(detachReferencesTo(…))`.  Forwarding them to onNodesChange
 *   would apply the removal a second time (double-remove).  Start removals
 *   must always be blocked: the Start node cannot be deleted.
 */
export function filterNodeChanges(
  changes: Array<{ type: string; id?: string }>,
  nodes: Array<{ id: string; type?: string }>,
): {
  removedIds: string[];
  filteredChanges: Array<{ type: string; id?: string }>;
} {
  const removedIds = changes
    .filter((c) => c.type === 'remove' && c.id !== undefined)
    .map((c) => c.id as string)
    .filter((id) => {
      const node = nodes.find((n) => n.id === id);
      // Exclude the Start node — it must never be deleted.
      return !(node && node.type === 'start');
    });

  // Strip ALL remove changes: non-start removals are handled by
  // detachReferencesTo; start removals are blocked.
  const filteredChanges = changes.filter((c) => c.type !== 'remove');

  return { removedIds, filteredChanges };
}

interface UseFlowEditorStateProps {
  initialFlow?: FlowDSL;
}

export function useFlowEditorState({ initialFlow }: UseFlowEditorStateProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const [simulatorFullscreen, setSimulatorFullscreen] = useState(false);
  const [initialState, setInitialState] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load initial flow data
  useEffect(() => {
    if (initialFlow && initialFlow.nodes) {
      // Guard: if the editor already has a known-clean baseline (initialState
      // is not null) AND the author has unsaved canvas edits (hasUnsavedChanges
      // is true), do NOT overwrite local state with the server snapshot.
      //
      // This prevents the post-save invalidateQueries refetch from wiping a
      // canvas edit (addPage, deleteNode, drag) that was made while the
      // previous auto-save was in-flight.  The guard is lifted on:
      //   - Initial mount (initialState === null)
      //   - After the author saves their canvas edits (hasUnsavedChanges becomes false)
      //   - Component remount (navigate away and back resets initialState to null)
      if (initialState !== null && hasUnsavedChanges) return;

      const { nodes: reactFlowNodes, edges: reactFlowEdges } = convertDSLToReactFlowNodes(initialFlow.nodes);

      // Ensure all nodes have unique IDs
      const seenNodeIds = new Set<string>();
      const uniqueNodes = reactFlowNodes.filter((node) => {
        if (seenNodeIds.has(node.id)) {
          console.warn(`Duplicate node ID found: ${node.id}`);
          return false;
        }
        seenNodeIds.add(node.id);
        return true;
      });

      // Ensure all edges have unique IDs
      const seenEdgeIds = new Set<string>();
      const uniqueEdges = reactFlowEdges.filter((edge) => {
        if (seenEdgeIds.has(edge.id)) {
          console.warn(`Duplicate edge ID found: ${edge.id}`);
          return false;
        }
        seenEdgeIds.add(edge.id);
        return true;
      });

      setNodes(uniqueNodes);
      setEdges(uniqueEdges);
      // Store initial state for comparison (without React Flow runtime fields).
      setInitialState(stableHash(uniqueNodes, uniqueEdges));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFlow, setNodes, setEdges]);
  // Note: initialState and hasUnsavedChanges are intentionally omitted from
  // deps.  Including them would re-run this effect whenever the author types,
  // causing React to re-seed nodes from the server on every edit.  The guard
  // reads their *current* values via closure; the ref-like read is safe because
  // we only need to check them once per new initialFlow value.

  // Track changes to nodes and edges
  useEffect(() => {
    if (initialState !== null) {
      setHasUnsavedChanges(stableHash(nodes, edges) !== initialState);
    }
  }, [nodes, edges, initialState]);

  // Prevent deleting Start node and handle page branch detachment
  const onNodesChangeWrapped = useCallback(
    (changes: any[]) => {
      const { removedIds, filteredChanges } = filterNodeChanges(changes, nodes);

      if (removedIds.length > 0) {
        // detachReferencesTo removes the nodes AND clears every branch reference
        // pointing at them across all pages.  We call setNodes directly so
        // React Flow's own change processing (filteredChanges below) doesn't
        // also try to remove them.
        setNodes((currentNodes) => detachReferencesTo(currentNodes, removedIds));
      }

      // Forward only non-remove changes: all removes (both Start and non-Start)
      // have been handled above or intentionally blocked.  Cast to any[] so
      // TypeScript accepts the minimally-typed shape returned by filterNodeChanges
      // (the objects are the original NodeChange values — just filtered).
      onNodesChange(filteredChanges as any[]);
    },
    [nodes, onNodesChange, setNodes]
  );

  // Handle edge deletion for page branches - detach instead of delete
  const onEdgesChangeWrapped = useCallback(
    (changes: any[]) => {
      const processedChanges = changes.map((change) => {
        if (change.type === 'remove') {
          const edge = edges.find((e) => e.id === change.id);
          if (!edge) return change;

          // Check if this is a page branch edge (contains -yes-, -no-, or -choice)
          const isPageBranchEdge = edge.id.includes('-yes-') || edge.id.includes('-no-') || edge.id.includes('-choice') || edge.id.includes('-button-');

          if (isPageBranchEdge) {
            // Find the source page node
            const pageNode = nodes.find((n) => n.id === edge.source);
            if (pageNode && pageNode.type === 'page' && pageNode.data.elements) {
              // Detach the branch by updating elements
              setNodes((currentNodes) =>
                currentNodes.map((n) => {
                  if (n.id !== pageNode.id) return n;

                  const updatedElements = n.data.elements.map((el: any) => {
                    if (!el.createBranches && !(el.type === 'Button' && el.action === 'navigate')) return el;

                    // Handle YesNo
                    if (el.type === 'YesNo' && el.branches) {
                      const updatedBranches = { ...el.branches };
                      if (edge.id.includes('-yes-') && updatedBranches.yes === edge.target) {
                        updatedBranches.yes = undefined;
                      }
                      if (edge.id.includes('-no-') && updatedBranches.no === edge.target) {
                        updatedBranches.no = undefined;
                      }
                      return { ...el, branches: updatedBranches };
                    }

                    // Handle MultiChoice
                    if (el.type === 'MultiChoice' && el.branches) {
                      const updatedBranches = el.branches.map((b: any) =>
                        b.targetPageId === edge.target ? { ...b, targetPageId: undefined } : b
                      );
                      return { ...el, branches: updatedBranches };
                    }

                    // Handle Button
                    if (el.type === 'Button' && el.targetPageId === edge.target) {
                      return { ...el, targetPageId: undefined };
                    }

                    return el;
                  });

                  // Return updated node with special marker to trigger stub regeneration
                  return { ...n, data: { ...n.data, elements: updatedElements, _regenerateStubs: true } };
                })
              );

              // Prevent the edge deletion since we're detaching
              return null;
            }
          }
        }
        return change;
      }).filter((change) => change !== null);

      onEdgesChange(processedChanges);
    },
    [edges, nodes, onEdgesChange, setNodes]
  );

  /**
   * Mark the canvas as clean.
   *
   * Accepts an optional `explicitHash` — the stableHash of the state that was
   * *actually sent to the server* in the save that just completed.  When
   * supplied, the editor uses that hash as the new baseline instead of hashing
   * the current canvas (which may already contain newer edits that were not
   * included in the save).  Those newer edits therefore remain dirty, keeping
   * the toolbar in "Save*" state rather than falsely showing "Saved".
   *
   * When called without an argument the current canvas hash is used, which is
   * the correct behaviour for e.g. createNewVersion (where the whole current
   * state is what was just saved).
   */
  const resetUnsavedChanges = useCallback(
    (explicitHash?: string) => {
      setInitialState(explicitHash ?? stableHash(nodes, edges));
      setHasUnsavedChanges(false);
    },
    [nodes, edges],
  );

  return {
    nodes,
    setNodes,
    onNodesChange: onNodesChangeWrapped,
    edges,
    setEdges,
    onEdgesChange: onEdgesChangeWrapped,
    selectedNode,
    setSelectedNode,
    simulatorOpen,
    setSimulatorOpen,
    simulatorFullscreen,
    setSimulatorFullscreen,
    hasUnsavedChanges,
    resetUnsavedChanges,
  };
}
